import type { DocumentCustomCategorieMapping } from '../../features/document-viewer/types';
import { getDocumentCategoryLabel, type Translate } from '@folio/utils/getDocumentCategoryLabel';
import type { CollectionDocument, DocumentGroup } from './DocumentsCollection';

type UploadedDocumentCategory = DocumentCustomCategorieMapping & { documentType?: string };

export function getUploadedDocumentGroups<TDocument extends CollectionDocument>({
  documents,
  categories,
  translate,
}: {
  documents: readonly TDocument[];
  categories: readonly UploadedDocumentCategory[];
  translate: Translate;
}): DocumentGroup<TDocument>[] {
  const declaredCategories = new Set(categories.map(({ category }) => category));
  const groupedDocuments = new Map<string, TDocument[]>();
  const unclassifiedDocuments: TDocument[] = [];
  for (const document of documents) {
    const customCategory = document.category ?? '';
    const category = declaredCategories.has(customCategory)
      ? customCategory
      : categories.find(({ documentType }) => documentType && documentType === document.category)
          ?.category;
    if (category !== undefined) {
      const existing = groupedDocuments.get(category) ?? [];
      existing.push(document);
      groupedDocuments.set(category, existing);
    } else {
      unclassifiedDocuments.push(document);
    }
  }
  const groups = categories.map(({ category, required }) => ({
    id: `category:${category}`,
    label: translate(`documentReview.category.${category}`, {
      defaultValue: translate(`documents.${category}`),
    }),
    requirements: getDocumentCategoryLabel(category, categories, translate),
    required,
    documents: groupedDocuments.get(category) ?? [],
  }));
  return [
    ...(unclassifiedDocuments.length
      ? [
          {
            id: 'uncategorized',
            label: translate('fileInput.uncategorizedFolderTitle'),
            documents: unclassifiedDocuments,
          },
        ]
      : []),
    ...groups,
  ];
}
